import React, { useState, useEffect } from 'react';
import './ComercialUserManagement.css';
import ComercialCreateUserModal from './ComercialCreateUserModal';
import ComercialEditUserModal from './ComercialEditUserModal';
import axios from 'axios';
import DEPLOYMENT_CONFIG from '../../config/deployment';
import ConfirmDialog from '../Shared/ConfirmDialog';
import { useCognitoAuth } from '../../contexts/CognitoAuthContext';
import { canResetUserPassword, isAdmin } from '../../utils/roleUtils';
import {
  formatDetailField,
  formatUserLastLogin,
  resolveUserAvatarUrl,
} from './userManagementDisplay';

type ComercialUserManagementProps = {
  departmentLabel?: string;
  heroSubtitle?: string;
  className?: string;
  teamOptions?: string[];
  forcedTeam?: string;
  /** Override API base (e.g. CCC app same-origin /api/ccc). */
  apiBaseUrl?: string;
  usersListPath?: string;
  createUserPath?: string;
  updateUserPath?: (userId: number) => string;
  usersListKeys?: string[];
  defaultDepartment?: string;
  showCreateButton?: boolean;
  /** CCC: POST /api/ccc/users/:id/resend-invitation (Cognito reset + invite). */
  resendInvitationPath?: (userId: number) => string;
  /** Override email update route (e.g. CCC /api/ccc/users/:id/update-email). */
  updateEmailPath?: (userId: number) => string;
  /** When true (CCC Gestão), show reset-password / edit-email for page managers even if tenant detection is incomplete. */
  enableManagerSensitiveActions?: boolean;
  /** Override delete route (e.g. CCC /api/ccc/users/:id). */
  deleteUserPath?: (userId: number) => string;
  /** Override suspend Cognito route (e.g. CCC /api/ccc/users/:id/suspend-cognito-access). */
  suspendUserPath?: (userId: number) => string;
  /** Origin for avatar/static paths (defaults to deployment backend URL). */
  resourceBaseUrl?: string;
  /** Hide Código Comercial row (legacy; CCC now shows this field). */
  hideCodigoComercial?: boolean;
  /** Role options for create/edit dropdowns (defaults to Comercial set). */
  roleOptions?: string[];
};

const ComercialUserManagement: React.FC<ComercialUserManagementProps> = ({
  departmentLabel = 'Comercial',
  heroSubtitle = 'Gerir e acompanhar a equipa comercial da Wondertrade',
  className = '',
  teamOptions = ['A', 'B', 'C'],
  forcedTeam,
  apiBaseUrl,
  usersListPath = '/api/callcenter/comercial-users',
  createUserPath = '/api/comercial/users',
  updateUserPath = (userId: number) => `/api/comercial/users/${userId}`,
  usersListKeys = ['comercial_users', 'users', 'data'],
  defaultDepartment = 'Comercial',
  showCreateButton = true,
  resendInvitationPath,
  updateEmailPath,
  enableManagerSensitiveActions = false,
  deleteUserPath,
  suspendUserPath,
  resourceBaseUrl,
  hideCodigoComercial = false,
  roleOptions = ['Gerente', 'Gestor', 'Assistente de Gestor', 'Instrutor', 'Assistente instrutor', 'Comercial'],
}) => {
  const avatarApiOrigin = resourceBaseUrl !== undefined ? resourceBaseUrl : DEPLOYMENT_CONFIG.getBackendUrl();
  const { user: currentUser, refreshUser } = useCognitoAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [resetPasswordConfirmOpen, setResetPasswordConfirmOpen] = useState(false);
  const [pendingResetPasswordId, setPendingResetPasswordId] = useState<number | null>(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [suspendConfirmOpen, setSuspendConfirmOpen] = useState(false);
  const [pendingSuspendId, setPendingSuspendId] = useState<number | null>(null);
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [editEmailModalOpen, setEditEmailModalOpen] = useState(false);
  const [editEmailUser, setEditEmailUser] = useState<any>(null);
  const [newEmail, setNewEmail] = useState('');
  const [editEmailLoading, setEditEmailLoading] = useState(false);
  const [editEmailError, setEditEmailError] = useState<string | null>(null);
  const [editEmailSuccess, setEditEmailSuccess] = useState<string | null>(null);

  const tenantFromStorage =
    typeof localStorage !== 'undefined' ? localStorage.getItem('user_tenant') : null;
  // Prefer session user; fall back to localStorage so CCC still shows actions if context user is empty.
  const sessionUserForActions = (() => {
    if (currentUser) return currentUser as { role?: string; tenant?: string; business_area?: string };
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem('user') || localStorage.getItem('cognito_user');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object'
        ? (parsed as { role?: string; tenant?: string; business_area?: string })
        : null;
    } catch {
      return null;
    }
  })();
  const canManageSensitiveActions =
    enableManagerSensitiveActions ||
    canResetUserPassword(sessionUserForActions, tenantFromStorage) ||
    isAdmin(sessionUserForActions?.role || '');
  const canShowResetPassword = canManageSensitiveActions;
  const canShowEditEmail =
    isAdmin(sessionUserForActions?.role || '') ||
    (Boolean(updateEmailPath) && canManageSensitiveActions);

  const roles = [
    'Gerente',
    'Gestor',
    'Assistente de Gestor',
    'Instrutor',
    'Assistente instrutor',
    'Comercial'
  ];

  const apiClient = React.useMemo(
    () =>
      axios.create({
        baseURL: apiBaseUrl !== undefined ? apiBaseUrl : DEPLOYMENT_CONFIG.getBackendUrl(),
        timeout: 30000,
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      }),
    [apiBaseUrl]
  );

  const getAuthToken = () => {
    return (
      localStorage.getItem('jwt_token') ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('cognito_access_token')
    );
  };

  const handleCreateUser = async () => {
    setError(null);
    setSuccess(null);
    setShowCreateForm(true);
  };

  const handleDeleteUser = (userId: number) => {
    setPendingDeleteId(userId);
    setConfirmOpen(true);
  };

  const handleSuspendAccess = (userId: number) => {
    setPendingSuspendId(userId);
    setSuspendConfirmOpen(true);
  };

  const confirmSuspendAccessNow = async () => {
    if (!pendingSuspendId) {
      setSuspendConfirmOpen(false);
      return;
    }
    try {
      setSuspendLoading(true);
      setError(null);
      setSuccess(null);
      const token = getAuthToken();
      const suspendPath = suspendUserPath
        ? suspendUserPath(pendingSuspendId)
        : `/api/users/${pendingSuspendId}/suspend-cognito-access`;
      const response = await apiClient.post(suspendPath, {}, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.data?.success) {
        setSuccess(response.data.message || 'Acesso suspenso.');
        setSuspendConfirmOpen(false);
        setPendingSuspendId(null);
        fetchUsers();
      } else {
        setError(response.data?.message || 'Erro ao suspender acesso.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao suspender acesso.');
    } finally {
      setSuspendLoading(false);
    }
  };

  const confirmDeleteNow = async () => {
    if (!pendingDeleteId) { setConfirmOpen(false); return; }
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const token = getAuthToken();
      const deletePath = deleteUserPath
        ? deleteUserPath(pendingDeleteId)
        : `/api/users/${pendingDeleteId}`;
      await apiClient.delete(deletePath, { headers: { 'Authorization': `Bearer ${token}` } });
      setSuccess('Utilizador movido para arquivo e desativado.');
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao remover utilizador.');
    } finally {
      setLoading(false);
      setConfirmOpen(false);
      setPendingDeleteId(null);
    }
  };

  const handleResetPassword = (userId: number) => {
    setPendingResetPasswordId(userId);
    setResetPasswordConfirmOpen(true);
  };

  const confirmResetPasswordNow = async () => {
    if (!pendingResetPasswordId) { setResetPasswordConfirmOpen(false); return; }
    try {
      setResetPasswordLoading(true);
      setError(null);
      setSuccess(null);
      const token = getAuthToken();
      const resetPath = resendInvitationPath
        ? resendInvitationPath(pendingResetPasswordId)
        : `/api/users/${pendingResetPasswordId}/admin-reset-password`;
      const response = await apiClient.post(resetPath, {}, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const msg = response.data?.message || 'Palavra-passe redefinida.';
      if (response.data?.email_sent === false) {
        setError(msg);
        setSuccess(null);
      } else {
        setSuccess(msg);
        setError(null);
      }
      setResetPasswordConfirmOpen(false);
      setPendingResetPasswordId(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao redefinir palavra-passe.');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleEditEmail = (user: any) => {
    setEditEmailUser(user);
    setNewEmail(user.email || '');
    setEditEmailError(null);
    setEditEmailSuccess(null);
    setEditEmailModalOpen(true);
  };

  const closeEditEmailModal = () => {
    setEditEmailModalOpen(false);
    setEditEmailUser(null);
    setNewEmail('');
    setEditEmailError(null);
    setEditEmailSuccess(null);
  };

  const confirmEditEmail = async () => {
    if (!editEmailUser || !newEmail.trim()) return;
    setEditEmailError(null);
    setEditEmailSuccess(null);
    try {
      setEditEmailLoading(true);
      const token = getAuthToken();
      const emailPath = updateEmailPath
        ? updateEmailPath(editEmailUser.id)
        : `/api/users/${editEmailUser.id}/update-email`;
      const response = await apiClient.put(emailPath, { email: newEmail.trim() }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setEditEmailSuccess(response.data.message || 'Email atualizado com sucesso!');
        fetchUsers();
        setTimeout(() => closeEditEmailModal(), 1500);
      } else {
        setEditEmailError(response.data.message || 'Erro ao atualizar email.');
      }
    } catch (err: any) {
      setEditEmailError(err?.response?.data?.message || 'Erro ao atualizar email.');
    } finally {
      setEditEmailLoading(false);
    }
  };

  const knownUiRoles = roleOptions.length ? roleOptions : roles;

  const normalizeRole = (raw: string | undefined | null, hint?: { username?: string; email?: string; name?: string }): string => {
    const trimmed = (raw || '').toString().trim();
    // Prefer exact UI labels from API (roleLabel) so Comercial / Instrutor round-trip correctly.
    if (knownUiRoles.includes(trimmed)) return trimmed;

    const v = trimmed.toLowerCase();
    if (/(call\s*center\s*manager|commercial\s*director|director|diretor|gerente\s*geral|gerente)/.test(v)) {
      return 'Gerente';
    }
    if (/(call\s*center\s*team\s*lead|sales\s*team\s*lead)/.test(v)) return 'Gestor';
    // "Assistente de Gestor" has "de" between words — \s*gestor alone never matched
    if (/(assistente\s+(?:de\s+)?gestor|assistant\s+manager)/.test(v)) return 'Assistente de Gestor';
    // Must check Assistente instrutor BEFORE bare "instrutor" (substring match).
    if (/(assistente\s*instrutor|assistant\s*trainer)/.test(v)) return 'Assistente instrutor';
    if (/(instrutor|trainer)/.test(v)) return 'Instrutor';
    if (/(manager|gestor)/.test(v)) return 'Gestor';
    // Prefer Comercial for Call Center Agent; Senior Agent → Assistente de Gestor.
    if (/call\s*center\s*senior\s*agent/.test(v)) return 'Assistente de Gestor';
    if (/call\s*center\s*agent/.test(v)) return 'Comercial';
    if (/(sales\s*team\s*lead)/.test(v)) return 'Gestor';
    if (/(comercial|sales\s*rep|representante|vendedor)/.test(v)) return 'Comercial';
    // Fallback: infer from username/email/name
    const h = `${hint?.username || ''} ${hint?.email || ''} ${hint?.name || ''}`.toLowerCase();
    if (h.includes('gerente') || h.includes('director') || h.includes('diretor')) return 'Gerente';
    if (/(assistente\s+(?:de\s+)?gestor)/.test(h)) return 'Assistente de Gestor';
    if (h.includes('gestor') || h.includes('manager')) return 'Gestor';
    return 'Comercial';
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      // Backend currently exposes comercial users via callcenter blueprint
      const res = await apiClient.get(usersListPath, { headers: { 'Authorization': `Bearer ${token}` } });
      let listRaw: unknown[] = [];
      for (const key of usersListKeys) {
        const candidate = res.data?.[key];
        if (Array.isArray(candidate)) {
          listRaw = candidate;
          break;
        }
      }
      const list = listRaw.filter((u: any) => {
        const uname = (u.username || u.name || '').toString().toLowerCase();
        const roleStr = (u.role || '').toString().toLowerCase();
        const emailStr = (u.email || '').toString().toLowerCase();
        if (uname.endsWith('_admin')) return false;
        if (roleStr.includes('admin')) return false;
        if (emailStr === 'comercial@wondertrade.pt') return false;
        return true;
      });
      const normalized = list.map((u: any, idx: number) => {
        const rawName = u.name || u.username || `${u.first_name || ''} ${u.last_name || ''}`.trim();
        const safeName = rawName || (u.email?.split('@')[0] || 'Utilizador');
        const initials = safeName.split(' ').filter(Boolean).map((p: string) => p[0]).slice(0, 2).join('').toUpperCase();
        const rawAvatarUrl = u.avatar_url || u.avatarUrl || u.avatarURL;
        const avatarSrcResolved = resolveUserAvatarUrl(
          typeof rawAvatarUrl === 'string' ? rawAvatarUrl : undefined,
          avatarApiOrigin
        );
        const teamValue =
          u.team || u.team_name || u.equipa || (forcedTeam ? forcedTeam : '');
        return {
          id: u.id || u.user_id || idx,
          name: safeName,
          email: u.email || '',
          role: normalizeRole(u.roleLabel || u.role || u.position || u.function || u.title, {
            username: u.username,
            email: u.email,
            name: safeName,
          }),
          department: defaultDepartment,
          lastLogin: formatUserLastLogin(u.lastLogin ?? u.last_login ?? u.last_seen),
          avatar: avatarSrcResolved || (typeof u.avatar === 'string' && (u.avatar.startsWith('data:') || u.avatar.startsWith('http')) ? u.avatar : undefined) || initials || 'C',
          phone: u.phone || u.phone_number || u.contact || '',
          emergencyContact: u.emergencyContact || u.emergency_contact || '',
          emergencyRelation: u.emergencyRelation || u.emergency_relation || '',
          team: teamValue,
          codigoComercial: u.codigoComercial || u.codigo_comercial || '',
          cognito_access_suspended_at: u.cognito_access_suspended_at || null,
        };
      });
      setUsers(normalized);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erro ao carregar utilizadores');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { fetchUsers(); }, []);

  // Ensure auth context has full user (role + tenant) from /api/users/me so reset button visibility is correct (once on mount)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only to avoid refresh loop
  useEffect(() => { refreshUser(); }, []);

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });


  return (
    <div className={`comercial-user-management ${className}`.trim()}>

      {/* Hero Header */}
      <div className="hero-header">
        <div className="hero-content">
          <div className="hero-left">
            <div className="hero-title-section">
              <div className="hero-title-with-badge">
                <h1 className="hero-title">Gestão de Utilizadores</h1>
                <span className="department-badge">{departmentLabel}</span>
              </div>
              <p className="hero-subtitle">{heroSubtitle}</p>
            </div>
          </div>
          {showCreateButton && (
            <div className="hero-right">
              <button className="create-user-btn" onClick={handleCreateUser}>
                <span className="btn-icon">+</span>
                <span>Novo Utilizador</span>
              </button>
            </div>
          )}
        </div>
      </div>


      {/* Enhanced Search and Filters */}
      <div className="search-section">
        <div className="search-container">
          <div className="search-input-wrapper">
            <input 
              type="text" 
              placeholder="Pesquisar por nome ou email..." 
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <select 
              className="filter-select"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
            >
              <option value="all">Todas as funções</option>
              <option value="Gerente">Gerente</option>
              <option value="Gestor">Gestor</option>
              <option value="Assistente de Gestor">Assistente de Gestor</option>
              <option value="Instrutor">Instrutor</option>
              <option value="Assistente instrutor">Assistente instrutor</option>
              <option value="Comercial">Comercial</option>
            </select>
          </div>
        </div>
      </div>

      {/* Modern User Cards Grid */}
      <div className="users-grid">
        {filteredUsers.map((user) => (
          <div key={user.id} className={`user-card${user.cognito_access_suspended_at ? ' user-card--access-suspended' : ''}`} onClick={() => setSelectedUser(user)}>
            <div className="user-card-header">
              <div className="user-avatar-large">
                {typeof user.avatar === 'string' && (user.avatar.startsWith('data:') || user.avatar.startsWith('http')) ? (
                  <img src={user.avatar} alt={user.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                ) : (
                  <>{user.avatar}</>
                )}
              </div>
            </div>
            
            <div className="user-card-body">
              <h3 className="user-name">{user.name}</h3>
              <p className="user-email">{user.email}</p>
              <div className="user-role-badge">{user.role}</div>
              {user.cognito_access_suspended_at && (
                <div className="access-suspended-badge" title="O utilizador não pode iniciar sessão; continua visível para revisão.">Acesso suspenso</div>
              )}
              
              <div className="user-details">
                <div className="detail-row">
                  <span className="detail-label">Contacto:</span>
                  <span className="detail-value">{formatDetailField(user.phone)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Contacto de Emergência:</span>
                  <span className="detail-value">{formatDetailField(user.emergencyContact)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Relação:</span>
                  <span className="detail-value">{formatDetailField(user.emergencyRelation)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Equipa:</span>
                  <span className="detail-value">{formatDetailField(user.team || forcedTeam)}</span>
                </div>
                {!hideCodigoComercial && (
                <div className="detail-row">
                  <span className="detail-label">Código Comercial:</span>
                  <span className="detail-value">{formatDetailField(user.codigoComercial)}</span>
                </div>
                )}
              </div>
            </div>
            
            <div className="user-card-footer">
              <div className="last-login">
                <span className="login-label">Último login:</span>
                <span className="login-date">{user.lastLogin}</span>
              </div>
              <div className="user-actions">
                <button
                  className="action-btn suspend-access"
                  title="Suspender início de sessão (Cognito)"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSuspendAccess(user.id);
                  }}
                >
                  ⏸
                </button>
                {canShowResetPassword && (
                  <button
                    className="action-btn reset-password"
                    title="Redefinir palavra-passe"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResetPassword(user.id);
                    }}
                  >
                    🔑
                  </button>
                )}
                {canShowEditEmail && (
                  <button
                    className="action-btn edit-email"
                    title="Editar Email"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditEmail(user);
                    }}
                  >
                    📧
                  </button>
                )}
                <button
                  className="action-btn edit"
                  title="Editar"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedUser(user);
                    setShowEditForm(true);
                  }}
                >
                  ✏️
                </button>
                <button 
                  className="action-btn delete" 
                  title="Remover"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteUser(user.id);
                  }}
                >
                  🗑️
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ComercialCreateUserModal
        visible={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        loading={loading}
        error={error}
        success={success}
        departmentLabel={departmentLabel}
        teamOptions={teamOptions}
        forcedTeam={forcedTeam}
        roleOptions={roleOptions}
        hideCodigoComercial={hideCodigoComercial}
        onCreate={async (payload) => {
          const token = getAuthToken();
          const requestData = {
            name: payload.name?.trim(),
            email: payload.email?.trim(),
            phone: payload.phone?.trim(),
            role: payload.role,
            team: forcedTeam || payload.team,
            emergencyContact: payload.emergencyContact?.trim() || undefined,
            emergencyRelation: payload.emergencyRelation?.trim() || undefined,
            codigoComercial: payload.codigoComercial?.trim() || undefined,
            avatar_base64: payload.profileImagePreview || undefined
          };
          try {
            setLoading(true);
            setError(null);
            setSuccess(null);
            const response = await apiClient.post(createUserPath, requestData, {
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (response.data.success) {
              const cognitoOk = response.data.cognito_synced !== false;
              const emailOk = response.data.email_sent !== false;
              if (!emailOk || !cognitoOk) {
                setError(
                  response.data.message ||
                    (!emailOk
                      ? 'Utilizador criado, mas o email de convite não foi enviado.'
                      : 'Utilizador criado, mas a sincronização Cognito falhou.')
                );
                setSuccess(null);
              } else {
                setSuccess(response.data.message || 'Utilizador criado com sucesso!');
                setError(null);
              }
              setShowCreateForm(false);
              fetchUsers();
            } else {
              setError(response.data.message || 'Erro ao criar utilizador');
            }
          } catch (err: any) {
            setError(err?.response?.data?.message || 'Erro ao criar utilizador. Tente novamente.');
          } finally {
            setLoading(false);
          }
        }}
      />

      <ComercialEditUserModal
        visible={showEditForm}
        onClose={() => setShowEditForm(false)}
        loading={loading}
        error={error}
        success={success}
        departmentLabel={departmentLabel}
        teamOptions={teamOptions}
        forcedTeam={forcedTeam}
        roleOptions={roleOptions}
        hideCodigoComercial={hideCodigoComercial}
        initial={selectedUser ? {
          id: selectedUser.id,
          name: selectedUser.name,
          email: selectedUser.email,
          phone: selectedUser.phone || '',
          role: selectedUser.role,
          team: selectedUser.team || '',
          emergencyContact: selectedUser.emergencyContact || '',
          emergencyRelation: selectedUser.emergencyRelation || '',
          codigoComercial: selectedUser.codigoComercial || '',
          profileImagePreview: (typeof selectedUser.avatar === 'string' && (selectedUser.avatar.startsWith('data:') || selectedUser.avatar.startsWith('http')))
            ? selectedUser.avatar
            : (selectedUser.avatarSrcResolved || null)
        } : undefined}
        onSave={async (payload) => {
          const token = getAuthToken();
          const requestData = {
            name: payload.name?.trim(),
            phone: payload.phone?.trim(),
            role: payload.role || undefined,
            team: forcedTeam || payload.team || undefined,
            emergency_contact: payload.emergencyContact?.trim() || undefined,
            emergency_relation: payload.emergencyRelation?.trim() || undefined,
            codigoComercial: payload.codigoComercial?.trim() || undefined,
            avatar_base64: payload.profileImagePreview || undefined
          };
          try {
            setLoading(true);
            setError(null);
            setSuccess(null);
            const response = await apiClient.put(updateUserPath(payload.id), requestData, {
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (response.data.success) {
              if (response.data.cognito_synced === false) {
                setError(response.data.message || 'Utilizador guardado, mas a sincronização Cognito falhou.');
                setSuccess(null);
              } else {
                setSuccess(response.data.message || 'Utilizador atualizado com sucesso!');
                setError(null);
              }
              setShowEditForm(false);
              fetchUsers();
            } else {
              setError(response.data.message || 'Erro ao atualizar utilizador');
            }
          } catch (err: any) {
            setError(err?.response?.data?.message || 'Erro ao atualizar utilizador. Tente novamente.');
          } finally {
            setLoading(false);
          }
        }}
      />

      <ConfirmDialog
        visible={suspendConfirmOpen}
        title="Suspender acesso (Cognito)"
        message="O utilizador deixará de poder iniciar sessão, mas permanecerá visível na lista para análise dos dados. Pode remover definitivamente mais tarde."
        confirmText="Suspender"
        cancelText="Cancelar"
        loading={suspendLoading}
        onConfirm={confirmSuspendAccessNow}
        onCancel={() => { setSuspendConfirmOpen(false); setPendingSuspendId(null); }}
      />

      <ConfirmDialog
        visible={confirmOpen}
        title="Remover utilizador (arquivo)"
        message="Remove o utilizador das listas e envia os dados para o arquivo. Se o acesso Cognito ainda estiver ativo, será desativado automaticamente antes do arquivo."
        confirmText="Remover"
        cancelText="Cancelar"
        loading={loading}
        onConfirm={confirmDeleteNow}
        onCancel={() => { setConfirmOpen(false); setPendingDeleteId(null); }}
      />

      <ConfirmDialog
        visible={resetPasswordConfirmOpen}
        title="Redefinir palavra-passe"
        message="Será enviado um email de convite ao utilizador com a palavra-passe temporária. Deseja continuar?"
        confirmText="Redefinir"
        cancelText="Cancelar"
        loading={resetPasswordLoading}
        onConfirm={confirmResetPasswordNow}
        onCancel={() => { setResetPasswordConfirmOpen(false); setPendingResetPasswordId(null); }}
      />

      {editEmailModalOpen && (
        <div style={{ position: 'fixed', inset: 0, backdropFilter: 'blur(2px)', background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }} onClick={closeEditEmailModal}>
          <div style={{ width: '94%', maxWidth: 480, background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.06)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: 24, background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#ffffff', position: 'relative' }}>
              <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(1200px 300px at -10% -20%, rgba(255,255,255,0.15), transparent)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: 0.2, color: '#ffffff' }}>Editar Email</h2>
                  <span style={{ background: 'rgba(255,255,255,0.18)', color: '#ffffff', padding: '4px 10px', borderRadius: 999, fontSize: 12 }}>
                    {departmentLabel}
                  </span>
                </div>
                <button aria-label="Fechar" onClick={closeEditEmailModal} style={{ background: 'rgba(255,255,255,0.18)', border: 0, color: '#ffffff', fontSize: 22, cursor: 'pointer', width: 36, height: 36, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
            </div>
            <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {editEmailError && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: 12, borderRadius: 8 }}>{editEmailError}</div>}
              {editEmailSuccess && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: 12, borderRadius: 8 }}>{editEmailSuccess}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label>Nome</label>
                <input value={editEmailUser?.name || editEmailUser?.username || ''} disabled style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 10, background: '#f9fafb', color: '#6b7280' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label>Email atual</label>
                <input value={editEmailUser?.email || ''} disabled style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 10, background: '#f9fafb', color: '#6b7280' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label>Novo Email <span style={{ color: '#e11d48' }}>*</span></label>
                <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="novo.email@exemplo.pt" autoFocus style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 10 }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 4 }}>
                <button type="button" onClick={closeEditEmailModal} disabled={editEmailLoading} style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#f3f4f6', cursor: 'pointer' }}>Cancelar</button>
                <button type="button" onClick={confirmEditEmail} disabled={editEmailLoading || !newEmail.trim() || newEmail.trim() === editEmailUser?.email} style={{ padding: '10px 16px', borderRadius: 10, border: 0, background: '#dc2626', color: '#fff', cursor: editEmailLoading || !newEmail.trim() || newEmail.trim() === editEmailUser?.email ? 'not-allowed' : 'pointer', opacity: editEmailLoading || !newEmail.trim() || newEmail.trim() === editEmailUser?.email ? 0.5 : 1 }}>{editEmailLoading ? 'A guardar...' : 'Atualizar Email'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComercialUserManagement;
